/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/BusinessEventRequirementCreation/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import styles from "./index.module.css";
import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import Select from "react-select";
import { CircularProgress } from "@mui/material";
import {
  getCategories
} from "../../logic/proposal/index.js";
import { getCertificationsOptions } from "../../data/selectOptions";
import {
  createFileObjectFromUrl,
  CERTIFICATION_OPTION_REQUIREMENT_PREFIX,
} from "../../utils/common.js";
import FileUploadArea from "../ProposalCreateModal/FileUploadArea";

/** One requirement row per certification option (same count/indices as {@link getCertificationsOptions}). */
function buildCertificationRequirementRows(t) {
  return getCertificationsOptions(t).map((opt) => ({
    key: `${CERTIFICATION_OPTION_REQUIREMENT_PREFIX}${opt.value}`,
    certificationOptionIndex: opt.value,
    value: 0,
  }));
}

/**
 * Default requirement rows for business-event requirements; certifications are listed after IMMEX.
 * @param {function(string): string} t - i18n `t`
 */
function getDefaultRequirements(t) {
  return [
    { name: "commercialNameLabel", key: "nombreComercial", value: 0 },
    { name: "socialReasonLabel", key: "razonSocial", value: 0 },
    { name: "economicSectorLabel", key: "sectorEconomico", value: 0 },
    { name: "subsectorLabel", key: "subSectorSCIAN", value: 0 },
    { name: "economicActivityLabel", key: "ramaSCIAN", value: 0 },
    { name: "businessGroupLabel", key: "grupoEmpresarial", value: 0 },
    { name: "descriptionLabel", key: "descripcionDeLaEmpresa", value: 0 },
    { name: "logoLabel", key: "logo", value: 0 },
    { name: "generalActivity", key: "actividadGeneralDeLaEmpresa", value: 0 },
    { name: "operationEnvironmentLabel", key: "ambitoOperaciones", value: 0 },
    { name: "companySizeLabel", key: "tamanoDeLaEmpresa", value: 0 },
    { name: "officePhoneLabel", key: "telefonoDeOficina", value: 0 },
    { name: "mainEmailAddressLabel", key: "correoElectronicoPrincipal", value: 0 },
    { name: "websiteLabel", key: "paginaInternet", value: 0 },
    { name: "townLabel", key: "domicilio2Municipio", value: 0 },
    { name: "stateLabel", key: "domicilio2Estado", value: 0 },
    { name: "countryLabel", key: "domicilio2Pais", value: 0 },
    { name: "exportsLabel", key: "exporta", value: 0 },
    { name: "countryExportingToLabel", key: "paisExportacion", value: 0 },
    { name: "importsLabel", key: "importa", value: 0 },
    { name: "countryImportingFromLabel", key: "paisImportacion", value: 0 },
    { name: "sellsToGovernment", key: "vendeAGobierno", value: 0 },
    { name: "brochureLabel", key: "brochure", value: 0 },
    { name: "taxIDLabel", key: "cedulaFiscal", value: 0 },
    { name: "positiveComplianceOpinionLabel", key: "opinionDeCumplimientoPositiva", value: 0 },
    { name: "civilLiabilityPolicyLabel", key: "polizaDeResponsabilidadCivil", value: 0 },
    { name: "IMSSRegistrationLabel", key: "altaIMSS", value: 0 },
    { name: "REPSELabel", key: "REPSE", value: 0 },
    { name: "proofOfCertificationLabel", key: "comprobanteDeCertificacion", value: 0 },
    { name: "sectorsAttendedLabel", key: "sectores", value: 0 },
    { name: "hasADocumentedQualityManagementSystemLabel", key: "tieneSistemaDocumentado", value: 0 },
    { name: "capacityAndTechnologiesLabel", key: "capacidadInstalada", value: 0 },
    { name: "REPSEProviderLabel", key: "esProveedorREPSE", value: 0 },
    { name: "averageDeliveryTimesLabel", key: "tiemposPromedioEntrega", value: 0 },
    { name: "acceptedPaymentMethodsLabel", key: "metodosDePagoAceptados", value: 0 },
    { name: "civilLiabilityInsuranceLabel", key: "seguroDeResponsabilidadCivil", value: 0 },
    { name: "IMMEXPermitLabel", key: "permisoIMMEX", value: 0 },
    ...buildCertificationRequirementRows(t),
  ];
}

/**
 * Merge saved requirement rows from the API with the current default shape (adds new certification rows, preserves values).
 * Aggregate row `key: certificaciones` applies its value to each per-certification row when present.
 */
function mergeBusinessEventRequirements(saved, defaults) {
  if (!saved || !Array.isArray(saved) || saved.length === 0) return defaults;
  const byKey = new Map(saved.map((r) => [r.key, r]));
  const aggregateCertificationsEntry = saved.find((r) => r.key === "certificaciones");
  return defaults.map((def) => {
    const existing = byKey.get(def.key);
    if (existing) return { ...def, value: existing.value };
    if (def.certificationOptionIndex !== undefined && aggregateCertificationsEntry) {
      return { ...def, value: aggregateCertificationsEntry.value };
    }
    return def;
  });
}

const BusinessEventRequirementCreation = ({
  dialogRef,
  participant,
  selectedRequirement,
  open,
  error,
  onCancel,
  onSubmit
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectCategoryRef = useRef(null);
  const selectSubcategoryRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileError, setFileError] = useState("");
  const [formData, setFormData] = useState({
    nombre: "",
    idUsuarioComprador: null,
    idCategoria: null,
    nombreSubcategoria: null,
    detalleTecnico: "",
    ejemploTecnico: null,
    nuevaImagenCargada: false,
    requisitos: getDefaultRequirements(t),
  });

  const fileRequirements = {
    maxSize: 5 * 1024 * 1024, // 5MB
    allowedTypes: ["image/jpeg", "image/jpg", "image/png"],
    errorMessages: {
      type: t("components.eventRequirementCreationModal.fileTypeError"),
      size: t("components.eventRequirementCreationModal.fileSizeError"),
    }
  }

  useEffect(() => {
    const fetchCategories = async () => {
      const categoriesData = await getCategories(navigate);
      setCategories(categoriesData);
      if (selectedRequirement) {
        const selectedCategory = categoriesData.find((category) => category._id === selectedRequirement.idCategoria);
        const activeSubcategories = selectedCategory?.subcategorias?.filter((subcategory) => subcategory.activo);
        setSubcategories(activeSubcategories);
        setFormData({
          _id: selectedRequirement._id,
          nombre: selectedRequirement.nombre || "",
          idUsuarioComprador: selectedRequirement.idUsuarioComprador || null,
          idCategoria: selectedRequirement.idCategoria || null,
          nombreSubcategoria: selectedRequirement.nombreSubcategoria || null,
          detalleTecnico: selectedRequirement.detalleTecnico || "",
          ejemploTecnico: createFileObjectFromUrl(selectedRequirement.ejemploTecnico) || null,
          nuevaImagenCargada: false,
          requisitos: mergeBusinessEventRequirements(
            selectedRequirement.requisitos,
            getDefaultRequirements(t),
          ),
        });
      }
    };

    const dialog = dialogRef.current;
    if (dialog) {
      if (open) {
        setIsSubmitting(false);
        setFormData({
          nombre: "",
          idUsuarioComprador: null,
          idCategoria: null,
          nombreSubcategoria: null,
          detalleTecnico: "",
          ejemploTecnico: null,
          nuevaImagenCargada: false,
          requisitos: getDefaultRequirements(t),
        });
        fetchCategories();

        if (selectCategoryRef.current)
          selectCategoryRef.current.clearValue();
        if (selectSubcategoryRef.current)
          selectSubcategoryRef.current.clearValue();

        dialog.showModal();
      } else {
        dialog.close();
      }
    }
  }, [open]);

  useEffect(() => {
    const handleClose = () => {
      onCancel();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
  }, [onCancel]);

  const handleCheckboxChange = (index, value) => {
    const newRequirements = formData.requisitos.map((req, rIdx) => {
      if (rIdx === index) {
        return {
          ...req,
          value: value
        };
      }
      return req;
    });

    setFormData({ ...formData, requisitos: newRequirements });
    setHasChanges(true);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileClick = (fileInputId) => {
    document.getElementById(fileInputId).click();
  };

  const validateFile = (file) => {
    if (!fileRequirements.allowedTypes.includes(file.type)) {
      setFileError(fileRequirements.errorMessages.type);
      return false;
    }

    if (file.size > fileRequirements.maxSize) {
      setFileError(fileRequirements.errorMessages.size);
      return false;
    }

    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (validateFile(file)) {
        setFileError("");
        setFormData((prev) => ({ 
            ...prev,
          ejemploTecnico: file,
          nuevaImagenCargada: true
        }));
        setHasChanges(true);
      } else {
        setFormData((prev) => ({ 
          ...prev,
          ejemploTecnico: null,
          nuevaImagenCargada: false
        }));
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      if (validateFile(file)) {
        setFileError("");
        setFormData((prev) => ({ ...prev,
          ejemploTecnico: file,
          nuevaImagenCargada: true
        }));
        setHasChanges(true);
      } else {
        setFormData((prev) => ({ ...prev,
          ejemploTecnico: null,
          nuevaImagenCargada: false
        }));
      }
    }
  };

  const checkSaveDisabled = () => {
    return (
      isSubmitting ||
      !hasChanges ||
      !formData.nombre ||
      formData.idCategoria == null ||
      formData.nombreSubcategoria == null ||
      formData.idUsuarioComprador == null ||
      !formData.detalleTecnico
    );
  };

  const handleSubmit = async () => {
    if (checkSaveDisabled()) return;
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
     <dialog ref={dialogRef} className={styles.requirementsModal}>
        <div className={styles.closeButtonContainer}>
          <button className={styles.closeButton} onClick={onCancel} disabled={isSubmitting}>
            &times;
          </button>
        </div>
        <div className={styles.modalHeader}>
          <span className={styles.title}>
            {t("components.eventRequirementCreationModal.title")}
          </span>
          <span className={styles.modalSubtitle}>
            {t("components.eventRequirementCreationModal.description")}
          </span>
        </div>
        <div className={styles.modalContent}>
          <div className={styles.formGrid}>
            <div className={styles.titleContainer}>
              <label className={styles.inputLabel}>
                {t("components.eventRequirementCreationModal.name")}
              </label>
              <input
                type="text"
                placeholder={t(
                  "components.eventRequirementCreationModal.namePlaceholder"
                )}
                className={styles.input}
                value={formData.nombre || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  handleChange("nombre", value);
                }}
                maxLength={100}
              />
            </div>
            <div className={styles.inputCol}>
              <label className={styles.inputLabel}>
                {t("components.eventRequirementCreationModal.assignedTo")}
              </label>
              <Select
                options={participant.ejecutivosCompradoresCompletos ?? []}
                isSearchable={false}
                value={(participant.ejecutivosCompradoresCompletos ?? []).find(
                  (option) => option._id === formData.idUsuarioComprador
                )}
                onChange={(selectedOption) => {
                  if (selectedOption) {
                    handleChange("idUsuarioComprador", selectedOption._id);
                  }
                }}
                placeholder={"Selecciona una empresa"}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: "8px",
                    width: "100%",
                    height: "40px",
                    fontSize: "16px",
                    cursor: "pointer",
                    textAlign: "left",
                    borderColor: "#909092",
                  }),
                  container: (baseStyles) => ({
                    ...baseStyles,
                    width: "100%",
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    cursor: "pointer",
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                }}
                getOptionValue={(option) => option._id}
                formatOptionLabel={(option) => <span>{option.nombre}</span>}
              />
            </div>
            <div className={styles.categoryContainer}>
              <label className={styles.inputLabel}>
                {t("components.eventRequirementCreationModal.category")}
              </label>
              <Select
                ref={selectCategoryRef}
                options={categories}
                isSearchable={false}
                value={categories.find(
                  (option) => option._id === formData.idCategoria
                )}
                onChange={(selectedOption) => {
                  if (selectedOption) {
                    setFormData((prev) => ({
                      ...prev,
                      idCategoria: selectedOption._id,
                      nombreSubcategoria: null,
                    }));
                    selectSubcategoryRef.current.clearValue();
                    setSubcategories(selectedOption?.subcategorias ?? []);
                  }
                }}
                placeholder={t(
                  "components.eventRequirementCreationModal.categoryPlaceholder"
                )}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: "8px",
                    width: "100%",
                    height: "30px",
                    fontSize: "16px",
                    cursor: "pointer",
                    borderColor: "#909092",
                  }),
                  container: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    cursor: "pointer",
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                }}
                getOptionValue={(option) => option._id}
                formatOptionLabel={(option) => <span>{option.nombre}</span>}
              />
            </div>
            <div className={styles.categoryContainer}>
              <label className={styles.inputLabel}>
                {t("components.eventRequirementCreationModal.subcategory")}
              </label>
              <Select
                ref={selectSubcategoryRef}
                options={subcategories}
                isDisabled={!formData.idCategoria}
                isSearchable={false}
                value={subcategories.find(
                  (option) => option.nombre === formData.nombreSubcategoria
                )}
                onChange={(selectedOption) => {
                  if (selectedOption)
                    handleChange("nombreSubcategoria", selectedOption.nombre);
                }}
                placeholder={t(
                  "components.eventRequirementCreationModal.subcategoryPlaceholder"
                )}
                styles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    borderRadius: "8px",
                    width: "100%",
                    height: "30px",
                    fontSize: "16px",
                    cursor: "pointer",
                    // marginBottom: "10px",
                    borderColor: "#909092",
                  }),
                  container: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  valueContainer: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                  }),
                  option: (baseStyles) => ({
                    ...baseStyles,
                    cursor: "pointer",
                  }),
                  indicatorSeparator: () => ({ display: "none" }),
                }}
                getOptionValue={(option) => option.nombre}
                formatOptionLabel={(option) => <span>{option.nombre}</span>}
              />
            </div>
          </div>

          <div className={styles.fullWidthContainer}>
            <label className={styles.inputLabel}>
              {t("components.requirementCreationModal.description")}
            </label>
            <textarea
              placeholder={t(
                "components.requirementCreationModal.descriptionPlaceholder"
              )}
              className={styles.textarea}
              style={{
                borderColor: "#909092",
              }}
              value={formData.detalleTecnico || ""}
              onChange={(e) => {
                const value = e.target.value;
                handleChange("detalleTecnico", value);
              }}
              maxLength={300}
            />
          </div>
          <div className={styles.fullWidthContainer}>
            <label className={styles.inputLabel}>
              {t("components.eventRequirementCreationModal.technicalExample")}
            </label>
            <div className={styles.fileUploadContainer}>
              <FileUploadArea
                file={formData.ejemploTecnico}
                isUploading={isUploading}
                isDragging={isDragging}
                error={fileError}
                onFileChange={(e) =>
                  handleFileChange(e)
                }
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e)}
                onClick={() => handleFileClick("requirementFileInput")}
                // reduceHeightForTablet={isTablet}
              />
              <input
                id="requirementFileInput"
                type="file"
                onChange={(e) => handleFileChange(e)}
                className={styles.fileInput}
                style={{ display: "none" }}
              />
            </div>
          </div>

          <div className={styles.tableContainer}>
            <table className={styles.requisitosTable}>
              <thead>
                <tr>
                  <th>{t("components.eventRequirementCreationModal.requirement")}</th>
                  <th>{t("components.eventRequirementCreationModal.optional")}</th>
                  <th>{t("components.eventRequirementCreationModal.notNecessary")}</th>
                  <th>{t("components.eventRequirementCreationModal.necessary")}</th>
                </tr>
              </thead>
              <tbody>
                {formData.requisitos.map((req, index) => (
                  <tr className={styles.requisitoRow} key={req.key}>
                    <td className={styles.requisitoName}>
                      {req.certificationOptionIndex !== undefined
                        ? t(
                            `components.certifications.certificationOption${
                              req.certificationOptionIndex + 1
                            }`,
                          )
                        : t(
                            `components.eventRequirementCreationModal.${req.name}`,
                          )}
                    </td>
                    <td>
                      <div className={styles.checkboxContainer}>
                        <input
                          className={styles.checkbox}
                          type="checkbox"
                          id="checkbox"
                          name={req.name}
                          checked={req.value === 0}
                          onClick={() => handleCheckboxChange(index, 0)}
                        />
                        <div className={styles.customCheckbox}></div>
                      </div>
                    </td>
                    <td>
                      <div className={styles.checkboxContainer}>
                        <input
                          className={styles.checkbox}
                          type="checkbox"
                          id="checkbox"
                          name={req.name}
                          checked={req.value === 1}
                          onClick={() => handleCheckboxChange(index, 1)}
                        />
                        <div className={styles.customCheckbox}></div>
                      </div>
                    </td>
                    <td>
                      <div className={styles.checkboxContainer}>
                        <input
                          className={styles.checkbox}
                          type="checkbox"
                          id="checkbox"
                          name={req.name}
                          checked={req.value === 2}
                          onClick={() => handleCheckboxChange(index, 2)}
                        />
                        <div className={styles.customCheckbox}></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* {error && (
            <div className={styles.errorMessage}>
              {t(`components.eventRequirementCreationModal.${error}`)}
            </div>
          )} */}
          {error === "IS_NAME_DUPLICATED" && (
            <div className={styles.errorMessage}>
              {t("errors.eventRequirementCreationModal.IS_NAME_DUPLICATED")}
            </div>
          )}
          <div className={styles.actions}>
            <button
              onClick={onCancel}
              className={styles.cancelButton}
              disabled={isSubmitting}
            >
              {t("components.eventRequirementCreationModal.cancelButton")}
            </button>
            <button
              className={`${styles.createButton} ${
                isSubmitting ? styles.createButtonSubmitting : ""
              }`}
              disabled={checkSaveDisabled()}
              onClick={handleSubmit}
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t("components.eventRequirementCreationModal.createButton")
              )}
            </button>
          </div>
        </div>   
     </dialog>
  );
};

export default BusinessEventRequirementCreation;